import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import Chart from '../chart';
import useAutoRefresh from '../hooks/useAutoRefresh';

export default function FeedToPlantChart({ range }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const { theme } = useTheme();
  const [data, setData] = useState([]);

  const fetchData = useCallback(async () => {
    const end = new Date();
    const start = new Date(end);
    if (range.unit === 'hour') {
      start.setHours(end.getHours() - range.value);
    } else {
      start.setDate(end.getDate() - range.value);
    }

    const maxPoints =
      range.unit === 'hour' && range.value === 1
        ? 60
        : range.unit === 'hour' && range.value === 24
        ? 48
        : range.unit === 'day' && range.value === 7
        ? 7
        : Math.max(12, range.value * (range.unit === 'hour' ? 4 : 1));
    const totalMs = end.getTime() - start.getTime();
    const stepMs = Math.max(1, Math.floor(totalMs / maxPoints));

    const baseline = 120;
    const amplitude = 18;
    const volatility = 6;
    const dummy = Array.from({ length: maxPoints }, (_, index) => {
      const time = new Date(start.getTime() + stepMs * index);
      const wave = Math.sin(index / 3) * amplitude;
      const drift = Math.cos(index / 5) * (amplitude / 2);
      const noise = (Math.random() - 0.5) * volatility;
      return {
        time: time.toISOString(),
        value: Math.round((baseline + wave + drift + noise) * 10) / 10,
      };
    });

    setData(dummy);
  }, [range]);

  useAutoRefresh(fetchData, [range]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const labels = data.map((d) => {
      const date = new Date(d.time);
      return range.unit === 'day'
        ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
        : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    });
    const values = data.map((d) => d.value);

    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            data: values,
              borderColor: '#2cc8de',
            backgroundColor: 'transparent',
            tension: 0.4,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500, easing: 'easeOutQuart' },
        plugins: { legend: { display: false }, datalabels: { display: false } },
        scales: {
          x: {
            ticks: {
              color: theme === 'dark' ? '#e5e7eb' : '#374151',
              maxTicksLimit: range.unit === 'day' ? 7 : 6,
            },
            grid: { display: false },
          },
          y: {
            ticks: { color: theme === 'dark' ? '#e5e7eb' : '#374151' },
            grid: { color: theme === 'dark' ? '#374151' : '#e5e7eb' },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [data, range.unit, theme]);

  return (
    <div className="relative w-full h-72">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
}
